// ============================================================
// VidyaTrack — Admin Dashboard
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiUsers, FiBook, FiBell, FiUser } from "react-icons/fi";
import { MdOutlineClass } from "react-icons/md";
import { useData } from "../../contexts/DataContext";
import { format, isPast } from "date-fns";
import StatCard from "../../components/shared/StatCard";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





export default function AdminDashboard() {
  const { getStudents, getTeachers, getClasses, getHomework, getAttendance, getNotices } = useData();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [homework, setHomework] = useState([]);
  const [att,      setAtt]      = useState([]);
  const [notices,  setNotices]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        const [st, tc, cl, hw, at, no] = await Promise.all([
          getStudents(), getTeachers(), getClasses(),
          getHomework(), getAttendance({}), getNotices({}),
        ]);
        setStudents(st); setTeachers(tc); setClasses(cl);
        setHomework(hw); setAtt(at); setNotices(no.slice(0,5));
      } catch { /* handled silently */ }
      finally { setLoading(false); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeHw  = homework.filter(h => !isPast(toDate(h.deadline)));
  const present   = att.filter(a => a.status === "present").length;
  const absent    = att.filter(a => a.status === "absent").length;
  const late      = att.filter(a => a.status === "late").length;
  const attTotal  = att.length;
  const attPct    = attTotal > 0 ? Math.round((present / attTotal) * 100) : 0;

  const attPie = [
    { name:"Present", value:present, color:"#059669" },
    { name:"Absent",  value:absent,  color:"#dc2626" },
    { name:"Late",    value:late,    color:"#d97706" },
  ].filter(d => d.value > 0);

  const hwBar = homework.slice(0,5).map(h => ({
    name: h.title.slice(0,10)+"…",
    pct : h.totalStudents > 0 ? Math.round(((h.submissionsCount||0)/h.totalStudents)*100) : 0,
  }));

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title">Admin Dashboard 🏫</h1>
          <p className="page-sub">{format(new Date(),"EEEE, MMMM d, yyyy")}</p>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <button className="btn btn-primary" onClick={() => navigate("/admin/notices")}><FiBell size={14}/> Post Notice</button>
          <button className="btn btn-secondary" onClick={() => navigate("/admin/reports")}>📊 Reports</button>
        </div>
      </div>

      <div className="g g4" style={{ marginBottom:20 }}>
        <StatCard icon={<FiUsers/>}       label="Students"         value={students.length} color="var(--blue-700)" bg="var(--blue-100)"  onClick={() => navigate("/admin/students")}/>
        <StatCard icon={<FiUser/>}        label="Teachers"         value={teachers.length} color="var(--green-600)"    bg="var(--green-50)"  onClick={() => navigate("/admin/teachers")}/>
        <StatCard icon={<MdOutlineClass/>} label="Classes"         value={classes.length}  color="#7c3aed"         bg="#f5f3ff"           onClick={() => navigate("/admin/classes")}/>
        <StatCard icon={<FiBook/>}        label="Active Homework"  value={activeHw.length} color="var(--amber-600)"   bg="var(--amber-50)"  onClick={() => navigate("/admin/homework")}/>
      </div>

      <div className="g g2" style={{ marginBottom:16 }}>
        {/* Attendance Pie */}
        <div className="card">
          <div className="card-header"><span className="card-title">📊 Attendance Overview</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/admin/attendance")}>Details</button>
          </div>
          <div className="card-body">
            {attTotal === 0 ? (
              <div className="empty-state" style={{ padding:"24px 0" }}>
                <div className="empty-icon">📊</div>
                <div className="empty-title">No attendance data yet</div>
                <div className="empty-desc">Attendance records will appear here once teachers start marking.</div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={attPie} dataKey="value" cx="50%" cy="50%" innerRadius={38} outerRadius={62}>
                      {attPie.map((e,i) => <Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius:8, fontSize:".8rem" }}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:"1.8rem", fontWeight:800, color:attPct>=75?"var(--green-600)":"var(--red-600)", fontFamily:"'Poppins',sans-serif", lineHeight:1, marginBottom:4 }}>{attPct}%</div>
                  <div style={{ fontSize:".76rem", color:"var(--gray-500)", marginBottom:10 }}>Overall Rate</div>
                  {attPie.map(d => (
                    <div key={d.name} style={{ display:"flex", justifyContent:"space-between", marginBottom:7 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ width:9, height:9, borderRadius:"50%", background:d.color }}/>
                        <span style={{ fontSize:".82rem" }}>{d.name}</span>
                      </div>
                      <span style={{ fontWeight:700, color:d.color, fontSize:".82rem" }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Homework Completion */}
        <div className="card">
          <div className="card-header"><span className="card-title">📚 Homework Completion</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/admin/homework")}>View All</button>
          </div>
          <div className="card-body">
            {hwBar.length === 0 ? (
              <div className="empty-state" style={{ padding:"24px 0" }}>
                <div className="empty-icon">📚</div>
                <div className="empty-title">No homework assigned yet</div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={hwBar} margin={{ top:5, right:10, bottom:32, left:-20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-20} textAnchor="end"/>
                  <YAxis tick={{ fontSize:10 }} domain={[0,100]}/>
                  <Tooltip contentStyle={{ borderRadius:8, fontSize:".8rem" }} formatter={v => [`${v}%`,"Completion"]}/>
                  <Bar dataKey="pct" fill="#054bb4" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:16, alignItems:"start" }}>
        {/* Quick Actions */}
        <div className="card">
          <div className="card-header"><span className="card-title">Admin Actions</span></div>
          <div className="card-body" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {[
              { icon:"👤", label:"Students",  path:"/admin/students"   },
              { icon:"👩‍🏫", label:"Teachers", path:"/admin/teachers"   },
              { icon:"🏫", label:"Classes",   path:"/admin/classes"    },
              { icon:"📢", label:"Notice",    path:"/admin/notices"    },
              { icon:"📅", label:"Calendar",  path:"/admin/calendar"   },
              { icon:"📊", label:"Reports",   path:"/admin/reports"    },
            ].map(a => (
              <button key={a.path} onClick={() => navigate(a.path)} style={{
                padding:"14px 8px", background:"var(--gray-100)",
                border:"1px solid var(--border)", borderRadius:"var(--r8)",
                cursor:"pointer", display:"flex", flexDirection:"column",
                alignItems:"center", gap:6, fontFamily:"inherit", transition:"var(--t)",
              }}
              onMouseEnter={e => { e.currentTarget.style.background="var(--blue-100)"; e.currentTarget.style.borderColor="var(--blue-700)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="var(--gray-100)"; e.currentTarget.style.borderColor="var(--border)"; }}
              >
                <span style={{ fontSize:"1.4rem" }}>{a.icon}</span>
                <span style={{ fontSize:".74rem", fontWeight:500 }}>{a.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Notices */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🔔 Notices</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/admin/notices")}>All</button>
          </div>
          <div className="card-body" style={{ padding:"10px 16px" }}>
            {notices.length === 0 ? (
              <div style={{ padding:"12px 0", textAlign:"center", color:"var(--gray-400)", fontSize:".84rem" }}>No notices yet</div>
            ) : notices.map(n => (
              <div key={n.id} style={{ padding:"10px 0", borderBottom:"1px solid #f0f0f0" }}>
                <div style={{ fontSize:".84rem", fontWeight:600 }}>{n.title}</div>
                <div style={{ fontSize:".72rem", color:"var(--gray-500)", marginTop:2 }}>{n.author}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width:1024px){
          .g2{grid-template-columns:1fr!important}
          div[style*="grid-template-columns:1fr 280px"]{grid-template-columns:1fr!important}
        }
      `}</style>

    </div>
  );
}